import { FC, useEffect, useState } from 'react';
import JSZip from 'jszip';
import styled from 'styled-components';
import LocalIcon, { LocalIconTypes } from '../../ui/LocalIcon';
import { orange } from '../../utils/style/colors';
import { useTranslation } from 'react-i18next';
import { getViewType } from '../../store/companies/companiesTypes';
import { FileViewer } from './FileViewer';
import { FileTypes } from '../../store/survey/surveyTypes';
import Icon from '../Icon/Icon';
import { languageDirection } from '../../i18n';
import {
  checkFileFormat,
  getMimeType,
} from '../../utils/check-file-type/checkFileFormat';
import Button from '../Button/Button';
import {
  LoadingContainer,
  LoadingProgress,
} from '../FileManager/ItemsViewer/DocumentViewerModal/styles';
import { convertDigitToProperFormat } from '../../utils/number';
import { createBlobFromAnUrl } from '../FileManager/ItemsViewer/DocumentViewerModal/DocumentViewModal';
import { getDomainAndPort, getProtocolDomainAndPort } from '../../api/Utils';

const SelectedItemContianer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: calc(100% - 1rem);
  gap: 0.5rem;
`;

const ZipViewerContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  flex-direction: column;
`;

const Navigation = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #fbe3b7;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  align-items: center;
`;

const Container = styled.div`
  overflow: auto;
  display: flex;
  align-items: start;
  justify-content: start;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 1rem 0;
`;

const Item = styled.div`
  height: 8rem;
  width: 8rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  word-break: break-all;
  transition: all ease-in-out 0.25s;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    cursor: pointer;
    background: #ffe7bc;
    //height: max-content;
    //overflow: visible;
  }
`;

const Content = styled.div``;

const IconContainer = styled.div``;

const CurrentDirection = styled.div`
  font-size: 1.2rem;
`;

// const Back = styled.button`
//   display: flex;
//   align-items: center;
//   gap: 0.5rem;
// `;

interface IZipViewer {
  fileUrl: string;
}

interface FileData {
  name: string;
  modified: string;
  isDir: boolean;
  zipEntry: any;
  size: number;
  format: string;
}

interface ISelectedItem {
  url: string;
  name: string;
  fullName: string;
  format: string;
}

const binaryTypes = [
  'pdf',
  'docx',
  'xlsx',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'mp4',
  'mp3',
  'zip',
  'woff',
  'ttf',
];

const ZipViewer: FC<IZipViewer> = ({ fileUrl }) => {
  const { t } = useTranslation('survey');

  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<ISelectedItem | null>(null);

  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const dir = languageDirection();

  useEffect(() => {
    const fetchZip = async () => {
      const blobUrl = await createBlobFromAnUrl(
        fileUrl,
        'applicatoin/zip',
        (p) => setProgress(p)
      );
      if (!blobUrl) return;
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const zip = await JSZip.loadAsync(blob);

      const fileEntries: FileData[] = [];
      const availableItems: string[] = [];
      zip.forEach((relativePath, file) => {
        const fileName = `${relativePath.split('/')[0]}/`;
        const suffix = relativePath.split('.').slice(-1)?.[0] || '';
        const format = getViewType(suffix);
        fileEntries.push({
          name: relativePath,
          modified: new Date().toISOString(),
          isDir: file.dir,
          format: file.dir ? 'folder' : checkFileFormat(format),
          size: (file as any)._data.uncompressedSize || 0,
          zipEntry: file,
        });
        availableItems.push(relativePath);
        if (!availableItems.includes(fileName)) {
          fileEntries.push({
            name: fileName,
            modified: new Date().toISOString(),
            isDir: true,
            format: 'folder',
            size: 0,
            zipEntry: file,
          });
          availableItems.push(fileName);
        }
      });

      setFiles(fileEntries);
      setLoading(false);
    };

    fetchZip();
  }, []);

  const handleSelectedItem = async (
    name: string,
    file: any,
    isDir: boolean
  ) => {
    if (isDir) {
      setCurrentPath((prevState) => [...prevState, name]);
    } else {
      const suffix = name.split('.').slice(-1)?.[0] || '';
      const format = getViewType(suffix);

      let blob: Blob;

      if (binaryTypes.includes(suffix)) {
        const buffer = await file.async('arraybuffer');
        blob = new Blob([buffer], { type: getMimeType(suffix) });
      } else {
        const text = await file.async('text');
        blob = new Blob([text], { type: 'text/plain' });
      }

      const url = URL.createObjectURL(blob);

      setSelectedItem({
        url,
        fullName: name,
        name: name.split(joinedPath).slice(-1)?.[0],
        format,
      });
    }
  };

  const joinedPath =
    currentPath.length > 0 ? currentPath?.[currentPath.length - 1] : '/';

  const arrowedJoinedPath = (currentPath?.[currentPath.length - 1] || '/')
    .split('/')
    .reverse()
    .join(dir === 'rtl' ? ' ← ' : ' → ');

  function getServerFileExtractorUrl(fileUrl: string, fullName: string) {
    const baseUrl = getProtocolDomainAndPort(getDomainAndPort());
    return `${baseUrl}/man/rest/file/exportFromZip?zipUrl=${encodeURIComponent(fileUrl)}&relativePath=${encodeURIComponent(fullName)}`;
  }

  if (selectedItem) {
    return (
      <SelectedItemContianer>
        <Navigation>
          <Button color={'black-outline'} onClick={() => setSelectedItem(null)}>
            <Icon type={'arrow-up'} />
            {t('back')}
          </Button>
          <div style={{ fontSize: '1.2rem' }}>{selectedItem.name}</div>
        </Navigation>
        <div style={{ overflow: 'auto', flexGrow: '1' }}>
          <FileViewer
            url={
              getViewType(selectedItem.format) === 'office'
                ? getServerFileExtractorUrl(fileUrl, selectedItem.fullName)
                : selectedItem.url
            }
            fileMimeType={selectedItem.format as FileTypes}
          />
        </div>
      </SelectedItemContianer>
    );
  }

  return (
    <ZipViewerContainer>
      <Navigation>
        {currentPath.length > 0 && (
          <Button
            color={'black-outline'}
            onClick={() =>
              setCurrentPath((prevState) => {
                return prevState.slice(0, -1) || [];
              })
            }
          >
            <Icon type={'arrow-up'} />
            {t('back')}
          </Button>
        )}
        <CurrentDirection>
          {arrowedJoinedPath.trim().length === 1
            ? '/'
            : arrowedJoinedPath.slice(3, arrowedJoinedPath.length)}
        </CurrentDirection>
      </Navigation>
      {loading ? (
        <LoadingContainer>
          <LoadingProgress>
            <div>
              {convertDigitToProperFormat(progress)}
              <span> % </span>
            </div>
            <div className="progress">
              <div className="determinate" style={{ width: progress + '%' }} />
            </div>
          </LoadingProgress>
        </LoadingContainer>
      ) : (
        <Container>
          {files
            .filter(
              (el) =>
                (el.isDir &&
                  el.name.slice(0, -1).split('/').length - 1 ===
                    currentPath.length) ||
                (!el.isDir &&
                  el.name.split('/').length - 1 === currentPath.length)
            )
            .filter((el) =>
              currentPath.length
                ? el.name.startsWith(currentPath.slice(-1)[0])
                : true
            )
            .map((el) => {
              return (
                <Item
                  key={el.name}
                  onClick={() =>
                    handleSelectedItem(el.name, el.zipEntry, el.isDir)
                  }
                >
                  <IconContainer>
                    <LocalIcon
                      iconName={
                        el.format === 'folder'
                          ? 'zip.svg'
                          : (el.format as LocalIconTypes)
                      }
                      color={orange}
                      isLink={true}
                    />
                  </IconContainer>
                  <Content>
                    {joinedPath?.length === 1
                      ? el.name.replace(/^\/+|\/+$/g, '')
                      : el.name
                          .split(joinedPath)
                          .slice(-1)?.[0]
                          .replace(/^\/+|\/+$/g, '')}
                  </Content>
                </Item>
              );
            })}

          <></>
        </Container>
      )}
    </ZipViewerContainer>
  );
};

export default ZipViewer;
